import React, { useState, useEffect, useRef } from 'react';
import { COMPANY_TYPES } from './constants';
import { API_BASE_URL } from '../../config';
import Cropper from 'react-easy-crop';

function CompanyInfoStep({ formData, handleInputChange, focusedField, isAnimating, handlePrevStep, handleNextStep, isReadOnly }) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dataFetchedRef = useRef(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const documentFileInputRefs = useRef({});
  
  useEffect(() => {
    // Only fetch data once
    if (dataFetchedRef.current) return;

    const fetchCompanyInfo = async () => {
      try {
        setIsLoading(true);
        setApiError(null);
        
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/company-info/fetch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ auth_key, user_id }),
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Update form data with fetched information
          if (data.company_info) {
            // Call the parent component's handleInputChange with each field
            Object.entries(data.company_info).forEach(([key, value]) => {
              if (value && formData.hasOwnProperty(key)) {
                handleInputChange({
                  target: {
                    name: key,
                    value: value
                  }
                }, { isFromApi: true });
              }
            });
          }
        } else {
          // If 404 is returned, it means company info doesn't exist yet which is fine
          if (response.status !== 404) {
            throw new Error(data.message || 'Failed to fetch company information');
          }
        }
        
        // Mark as fetched to prevent further fetches
        dataFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching company info:', error);
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyInfo();
  }, [formData, handleInputChange]);
  
  // Validation patterns
  const validationPatterns = {
    companyPAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    personalPAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    trustPAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    societyPAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    cin: /^[A-Z0-9]{21}$/,
    llpin: /^[A-Za-z]{3}-[0-9]{5}$/
  };
  
  // Helper function to validate URL format
  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  // Validation error messages
  const errorMessages = {
    entityType: 'Please select an entity type',
    incorporationDate: 'Please enter a valid incorporation date',
    companyPAN: 'Please enter a valid Company PAN (e.g., ABCDE1234F)',
    personalPAN: 'Please enter a valid Personal PAN (e.g., ABCDE1234F)',
    trustPAN: 'Please enter a valid Trust PAN (10 alphanumeric characters, e.g., ABCDE1234F)',
    societyPAN: 'Please enter a valid Society PAN (10 alphanumeric characters, e.g., ABCDE1234F)',
    cin: 'Please enter a valid CIN (21 alphanumeric characters)',
    llpin: 'Please enter a valid LLPIN (e.g., AAA-12345)',
    partnershipDeed: 'Please upload the Partnership Deed',
    hufDeed: 'Please upload the HUF Deed',
    trustDeed: 'Please upload the Trust Deed',
    registrationCertificate: 'Please upload the Registration Certificate',
    signature: 'Please upload your signature'
  };
  
  const validateField = (name, value) => {
    if (!value || value.trim() === '') {
      return errorMessages[name] || 'This field is required';
    }
    
    if (validationPatterns[name] && !validationPatterns[name].test(value)) {
      return errorMessages[name];
    }
    
    return '';
  };
  
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validate common required fields
    if (!formData.entityType) {
      newErrors.entityType = errorMessages.entityType;
      isValid = false;
    }
    
    if (!formData.incorporationDate) {
      newErrors.incorporationDate = errorMessages.incorporationDate;
      isValid = false;
    }
    
    // Signature validation - check if the signature exists and is a valid URL or file path
    if (!formData.signature || formData.signature.trim() === '') {
      newErrors.signature = errorMessages.signature;
      isValid = false;
    }
    
    // Validate entity type specific fields
    if (formData.entityType && COMPANY_TYPES[formData.entityType]) {
      const requiredFields = COMPANY_TYPES[formData.entityType].requiredFields;
      
      requiredFields.forEach(field => {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      });
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  // Enhanced input change handler with real-time validation
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    
    // Transform PAN and CIN to uppercase
    let transformedValue = value;
    if (['companyPAN', 'personalPAN', 'trustPAN', 'societyPAN', 'cin'].includes(name)) {
      transformedValue = value.toUpperCase();
    }
    
    // Call the parent's handleInputChange function with transformed value
    handleInputChange({
      target: {
        name,
        value: transformedValue
      }
    });
    
    // Clear error when user starts typing
    setErrors(prev => {
      const updatedErrors = { ...prev };
      
      // For entity type, validate specific fields when changed
      if (name === 'entityType' && transformedValue) {
        // Remove errors for fields that are no longer required
        Object.keys(updatedErrors).forEach(key => {
          if (['companyPAN', 'personalPAN', 'trustPAN', 'societyPAN', 'cin', 'llpin', 
               'partnershipDeed', 'hufDeed', 'trustDeed', 'registrationCertificate'].includes(key)) {
            delete updatedErrors[key];
          }
        });
      } else {
        // Validate the current field if it's required
        if (name !== 'needTdsBenefits') { // Skip validation for optional fields
          const error = validateField(name, transformedValue);
          if (error) {
            updatedErrors[name] = error;
          } else {
            delete updatedErrors[name];
          }
        }
      }
      
      return updatedErrors;
    });
  };
  
  const handleSaveProgress = async () => {
    if (isReadOnly) return;
    
    setIsSubmitting(true);
    setSaveSuccess(false);
    setApiError(null);

    try {
      // Get auth details from localStorage or context
      const auth_key = localStorage.getItem('authKey');
      const user_id = localStorage.getItem('userId');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      // Check if signature is provided before saving
      if (!formData.signature || formData.signature.trim() === '') {
        setErrors(prev => ({
          ...prev,
          signature: errorMessages.signature
        }));
        throw new Error('Please upload your signature before saving');
      }

      const payload = {
        ...formData,
        auth_key,
        user_id
      };

      const response = await fetch(`${API_BASE_URL}/brands/onboarding/company-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // If successful, show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleNext = async (e) => {
    if (isReadOnly) {
      handleNextStep();
      return;
    }
    
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setApiError(null);

      try {
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const payload = {
          ...formData,
          auth_key,
          user_id
        };

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/company-info`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        // If successful, move to next step
        handleNextStep();
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.querySelector(`[name="${firstErrorField}"]`)?.focus();
      }
    }
  };

  const handleDocumentUploadClick = (fieldName) => {
    if (isReadOnly) return;
    if (documentFileInputRefs.current[fieldName]) {
      documentFileInputRefs.current[fieldName].click();
    }
  };

  const handleDocumentFileSelect = async (e, fieldName) => {
    if (isReadOnly) return;
    
    const file = e.target.files[0];
    if (!file) return;

    // Validate file is a PDF
    if (file.type !== 'application/pdf') {
      setApiError('Please select a PDF file');
      return;
    }

    // Upload the document
    await uploadDocument(file, fieldName);
  };

  const uploadDocument = async (file, fieldName) => {
    if (isReadOnly) return;
    
    try {
      setIsSubmitting(true);
      setApiError(null);
      setUploadProgress(0);

      // Get auth details from localStorage
      const auth_key = localStorage.getItem('authKey');
      const user_id = localStorage.getItem('userId');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      // Create form data
      const formData = new FormData();
      formData.append('signature', file); // The API still expects 'signature' as the field name
      formData.append('auth_key', auth_key);
      formData.append('user_id', user_id);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Setup progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });
      
      // Create a promise to handle the XHR response
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } else {
            reject(new Error('Upload failed: ' + xhr.statusText));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error during upload'));
        };
      });
      
      // Send the request
      xhr.open('POST', `${API_BASE_URL}/brands/onboarding/docs/upload`, true);
      xhr.send(formData);
      
      const response = await uploadPromise;
      
      if (response.status === 'success') {
        const documentUrl = response.data?.document_url;
        
        if (!documentUrl) {
          throw new Error('No document URL returned from server');
        }
        
        // Clear any existing error
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
        
        // Update form data with document URL
        handleInputChange({
          target: {
            name: fieldName,
            value: documentUrl
          }
        });
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const renderCompanyFields = () => {
    if (!formData.entityType) return null;

    const selectedType = COMPANY_TYPES[formData.entityType];
    if (!selectedType) return null;

    return selectedType.requiredFields.map(field => {
      const fieldLabel = {
        companyPAN: 'Company PAN',
        personalPAN: 'Personal PAN',
        trustPAN: 'Trust PAN',
        societyPAN: 'Society PAN',
        cin: 'Corporate Identity Number (CIN)',
        llpin: 'LLP Identification Number (LLPIN)',
        partnershipDeed: 'Partnership Deed',
        hufDeed: 'HUF Deed',
        trustDeed: 'Trust Deed',
        registrationCertificate: 'Registration Certificate'
      }[field];

      const fieldPlaceholder = {
        companyPAN: 'Enter 10-character PAN (e.g., ABCDE1234F)',
        personalPAN: 'Enter 10-character PAN (e.g., ABCDE1234F)',
        trustPAN: 'Enter 10-character PAN (e.g., ABCDE1234F)',
        societyPAN: 'Enter 10-character PAN (e.g., ABCDE1234F)',
        cin: 'Enter 21-character CIN',
        llpin: 'Enter LLPIN (e.g., AAA-12345)',
        partnershipDeed: `Upload ${fieldLabel} (PDF only)`,
        hufDeed: `Upload ${fieldLabel} (PDF only)`,
        trustDeed: `Upload ${fieldLabel} (PDF only)`,
        registrationCertificate: `Upload ${fieldLabel} (PDF only)`
      }[field];

      const isFileUpload = ['partnershipDeed', 'hufDeed', 'trustDeed', 'registrationCertificate'].includes(field);

      return (
        <div key={field} className={`form-group ${focusedField === field ? 'focused' : ''}`}>
          <label>{fieldLabel}<span className="required"></span></label>
          {isFileUpload ? (
            <div className="file-input">
              <input
                type="text"
                name={field}
                value={formData[field] || ''}
                onChange={handleFormInputChange}
                placeholder={fieldPlaceholder}
                readOnly
                style={{ 
                  flex: 1,
                  cursor: 'default',
                  backgroundColor: formData[field] ? '#f0f8ff' : '',
                  border: formData[field] ? '1px solid #4CAF50' : '1px solid #ddd'
                }}
              />
              <button 
                type="button"
                className="upload-button"
                onClick={() => handleDocumentUploadClick(field)}
                disabled={isReadOnly || isSubmitting}
                style={{
                  minWidth: isSubmitting ? '130px' : '80px',
                  backgroundColor: formData[field] ? '#4CAF50' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 15px',
                  cursor: isReadOnly ? 'default' : isSubmitting ? 'default' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {isReadOnly ? 'View' : isSubmitting ? `Uploading... ${uploadProgress}%` : 
                 formData[field] ? 'Change' : 'Upload'}
              </button>
              <input
                ref={el => documentFileInputRefs.current[field] = el}
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => handleDocumentFileSelect(e, field)}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <input
              type="text"
              name={field}
              value={formData[field] || ''}
              onChange={handleFormInputChange}
              placeholder={fieldPlaceholder}
              required={!isReadOnly}
              readOnly={isReadOnly}
            />
          )}
          {formData[field] && isFileUpload && (
            <div className="document-preview" style={{
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '10px',
              marginTop: '10px',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ 
                color: '#4CAF50', 
                fontSize: '16px'
              }}>✓</span>
              <span>Document uploaded successfully</span>
              <a 
                href={formData[field]} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  marginLeft: 'auto',
                  color: '#2196F3',
                  textDecoration: 'none'
                }}
              >
                View Document
              </a>
            </div>
          )}
          {errors[field] && (
            <span className="error-message" style={{ 
              display: 'block', 
              marginTop: '5px',
              color: '#f44336',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {errors[field]}
            </span>
          )}
        </div>
      );
    });
  };

  // Signature Upload Functions
  const handleSignatureUploadClick = () => {
    if (isReadOnly) return;
    fileInputRef.current?.click();
  };

  const handleSignatureFileSelect = (e) => {
    if (isReadOnly) return;
    const file = e.target.files[0];
    if (!file) return;

    // Validate file is an image
    if (!file.type.startsWith('image/')) {
      setApiError('Please select a valid image file');
      return;
    }

    // Create a preview of the image
    const imageUrl = URL.createObjectURL(file);
    setSignaturePreview(imageUrl);
    setShowSignatureModal(true);
  };

  const handleCropComplete = (croppedImage) => {
    // This function will be called after cropping is complete
    setSignaturePreview(croppedImage);
    uploadSignature(croppedImage);
    setShowSignatureModal(false);
  };

  const handleCropCancel = () => {
    // Reset signature state
    setSignaturePreview(null);
    setShowSignatureModal(false);
  };

  const uploadSignature = async (imageFile) => {
    if (isReadOnly) return;
    
    try {
      setIsUploadingSignature(true);
      setApiError(null);
      setUploadProgress(0);

      // Get auth details from localStorage
      const auth_key = localStorage.getItem('authKey');
      const user_id = localStorage.getItem('userId');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      // Create form data
      const formData = new FormData();
      formData.append('signature', imageFile);
      formData.append('auth_key', auth_key);
      formData.append('user_id', user_id);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Setup progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });
      
      // Create a promise to handle the XHR response
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            const response = JSON.parse(xhr.responseText);
            console.log('Signature upload response:', response); // Debug log
            resolve(response);
          } else {
            console.error('Signature upload failed with status:', xhr.status, xhr.statusText);
            reject(new Error('Upload failed: ' + xhr.statusText));
          }
        };
        
        xhr.onerror = function() {
          console.error('Network error during signature upload');
          reject(new Error('Network error during upload'));
        };
      });
      
      // Send the request
      xhr.open('POST', `${API_BASE_URL}/brands/onboarding/signature/upload`, true);
      console.log('Sending signature upload request to:', `${API_BASE_URL}/brands/onboarding/signature/upload`);
      xhr.send(formData);
      
      const response = await uploadPromise;
      
      if (response.status === 'success') {
        // Try to find the signature URL in various common response formats
        const signatureUrl = response.signature_url || 
                             response.data?.signature_url || 
                             response.url || 
                             response.data?.url;
        
        console.log('Signature URL from response:', signatureUrl); // Debug log
        
        if (!signatureUrl) {
          console.error('No signature URL in response:', response);
          
          // If response contains a data object but no clear URL, try to find it
          if (response.data && typeof response.data === 'object') {
            // Look for any property that might contain a URL
            const possibleUrlKeys = Object.keys(response.data).filter(key => 
              typeof response.data[key] === 'string' && 
              (key.includes('url') || key.includes('link') || key.includes('path'))
            );
            
            if (possibleUrlKeys.length > 0) {
              const foundUrl = response.data[possibleUrlKeys[0]];
              console.log('Found possible URL in response:', foundUrl);
              
              // Update form data with found URL
              handleInputChange({
                target: {
                  name: 'signature',
                  value: foundUrl
                }
              });
              
              setSaveSuccess(true);
              setTimeout(() => setSaveSuccess(false), 3000);
              return;
            }
          }
          
          throw new Error('No signature URL returned from server');
        }
        
        // Validate URL format - make this optional if URLs might be relative paths
        if (!isValidURL(signatureUrl) && !signatureUrl.startsWith('/')) {
          console.warn('Non-standard URL format:', signatureUrl);
          // Continue anyway since it might be a relative path
        }
        
        // Clear any existing signature error
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.signature;
          return newErrors;
        });
        
        // Update form data with signature URL
        handleInputChange({
          target: {
            name: 'signature',
            value: signatureUrl
          }
        });
        
        console.log('Form data after signature update:', formData); // Debug log
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        console.error('Signature upload returned non-success status:', response);
        throw new Error(response.message || 'Failed to upload signature');
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      setApiError(error.message);
    } finally {
      setIsUploadingSignature(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="onboarding-section company-info">
      <div className="basic-container">
        <h1 className="gstin-title">Company Information</h1>

        {apiError && (
          <div className="api-error-message">
            {apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="success-message">
            Your progress has been saved successfully!
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <p>Loading your information...</p>
          </div>
        ) : (
          <>
            <section className="company-information">
              <div className="form-row">
                <div className={`form-group ${focusedField === 'entityType' ? 'focused' : ''}`}>
                  <label>Entity Type<span className="required"></span></label>
                  <select
                    name="entityType"
                    value={formData.entityType}
                    onChange={handleFormInputChange}
                    disabled={isReadOnly}
                  >
                    <option value="">Select Entity Type</option>
                    {Object.entries(COMPANY_TYPES).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  {errors.entityType && <span className="error-message">{errors.entityType}</span>}
                </div>
                <div className={`form-group ${focusedField === 'incorporationDate' ? 'focused' : ''}`}>
                  <label>Date of Incorporation<span className="required"></span></label>
                  <input
                    type="date"
                    name="incorporationDate"
                    value={formData.incorporationDate}
                    onChange={handleFormInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    readOnly={isReadOnly}
                  />
                  {errors.incorporationDate && <span className="error-message">{errors.incorporationDate}</span>}
                </div>
              </div>

              <div className="form-row company-specific-fields">
                {renderCompanyFields()}
              </div>
   
              <div className="form-row">
                <div className={`form-group signature-group ${focusedField === 'signature' ? 'focused' : ''}`}>
                  <label>SIGNATURE<span className="required"></span></label>
                  <div className="signature-input" style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <input
                      type="text"
                      name="signature"
                      value={formData.signature || ''}
                      onChange={handleFormInputChange}
                      placeholder="Upload your signature"
                      readOnly={isReadOnly}
                      style={{ 
                        flex: 1,
                        cursor: 'default',
                        backgroundColor: formData.signature ? '#f0f8ff' : '',
                        border: formData.signature ? '1px solid #4CAF50' : '1px solid #ddd'
                      }}
                    />
                    <button 
                      type="button"
                      className="upload-button"
                      onClick={handleSignatureUploadClick}
                      disabled={isReadOnly || isUploadingSignature}
                      style={{
                        minWidth: isUploadingSignature ? '130px' : '80px',
                        backgroundColor: formData.signature ? '#4CAF50' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 15px',
                        cursor: isReadOnly ? 'default' : isUploadingSignature ? 'default' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isReadOnly ? 'View' : isUploadingSignature ? `Uploading... ${uploadProgress}%` : 
                       formData.signature ? 'Change' : 'Upload'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureFileSelect}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {formData.signature && (
                    <div className="signature-preview" style={{
                      border: '1px solid #eee',
                      borderRadius: '4px',
                      padding: '10px',
                      backgroundColor: '#f9f9f9',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <img 
                        src={formData.signature} 
                        alt="Signature" 
                        style={{ maxHeight: '100px', maxWidth: '100%' }}
                      />
                      <div style={{ 
                        marginTop: '5px', 
                        color: '#4CAF50', 
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span style={{ fontSize: '16px' }}>✓</span>
                        <span>Signature uploaded successfully</span>
                      </div>
                    </div>
                  )}
                  {errors.signature && (
                    <span className="error-message" style={{ 
                      display: 'block', 
                      marginTop: '5px',
                      color: '#f44336',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {errors.signature}
                    </span>
                  )}
                </div>
              </div>
            </section>

            <div className="form-actions">
              {!isReadOnly && (
                <button 
                  className="save-progress-button"
                  onClick={handleSaveProgress}
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? 'Saving...' : 'Save Progress'}
                </button>
              )}
              <div className="navigation-buttons">
                <button 
                  className="back-button"
                  onClick={handlePrevStep}
                  disabled={isSubmitting || isLoading}
                >
                  Back
                </button>
                <button 
                  className="next-button"
                  onClick={handleNext}
                  disabled={isSubmitting || isAnimating || isLoading}
                >
                  {isSubmitting ? 'Submitting...' : 'Next'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Signature Cropping Modal */}
      {showSignatureModal && signaturePreview && (
        <SignatureCropModal
          image={signaturePreview}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

// Signature Cropping Modal Component
function SignatureCropModal({ image, onCropComplete, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  const onCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const onZoomChange = (newZoom) => {
    setZoom(newZoom);
  };

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      onCropComplete(croppedImage);
    } catch (e) {
      console.error('Error creating cropped image:', e);
    }
  };

  // Function to create the cropped image
  const getCroppedImg = (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      
      image.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to cropped area size
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        
        // Draw the cropped image
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          // Create a File object from the blob
          const croppedFile = new File([blob], 'signature.png', { type: 'image/png' });
          resolve(croppedFile);
        }, 'image/png');
      };

      image.onerror = () => {
        reject(new Error('Error loading image'));
      };
    });
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div className="crop-container" style={{
        width: '90%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <div className="crop-header" style={{
          padding: '16px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>Crop Signature</h3>
          <button 
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ 
          position: 'relative',
          width: '100%',
          height: '300px',
          backgroundColor: '#f0f0f0'
        }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={3/1}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={handleCropComplete}
          />
        </div>

        <div style={{
          padding: '16px',
          borderTop: '1px solid #eee'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
              Zoom
            </label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => onZoomChange(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #eee'
        }}>
          <button 
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={createCroppedImage}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#2196F3',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompanyInfoStep; 